import { useEffect, useId, useMemo, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { LedgerEntry, Recipient } from "../../api/types";
import { UserPicker, type UserPickerValue } from "../../components/UserPicker";
import { useIdempotencyKey } from "../../lib/idempotencyKey";

interface GiveModalProps {
  open: boolean;
  availableCoins: number;
  seasonID: string | null;
  // isAnonymous reflects the giver's per-season choice. When true, the
  // message field is hidden — anonymous gumballs never carry a note since
  // notes leak identity, and the backend strips reasons on anon spends
  // anyway. The badge in the header reminds the user how they'll appear.
  isAnonymous: boolean;
  onClose: () => void;
  onSent: (entry: LedgerEntry) => void;
}

interface FormState {
  recipient: UserPickerValue;
  amount: string;
  reason: string;
}

interface RecipientsState {
  status: "loading" | "ready" | "error";
  recipients: Recipient[];
  errorMessage: string | null;
}

const initialForm: FormState = {
  recipient: { query: "", selectedID: "" },
  amount: "1",
  reason: ""
};
const initialRecipients: RecipientsState = { status: "loading", recipients: [], errorMessage: null };

export function GiveModal({
  open,
  availableCoins,
  seasonID,
  isAnonymous,
  onClose,
  onSent
}: GiveModalProps) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipientsState, setRecipientsState] = useState<RecipientsState>(initialRecipients);
  const { keyFor, reset } = useIdempotencyKey();
  const amountID = useId();
  const reasonID = useId();
  const overlayDismiss = useOverlayDismiss(onClose, submitting);

  // Reset on close so a re-open starts fresh and the next idempotency key
  // is generated against an empty payload.
  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setSubmitting(false);
      setError(null);
      reset();
    }
  }, [open, reset]);

  // Fetch recipients when the modal opens. Cancellable so a re-open before
  // the previous request returns doesn't clobber state.
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setRecipientsState({ ...initialRecipients });
    apiClient
      .listRecipients()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setRecipientsState({
          status: "ready",
          recipients: response.recipients,
          errorMessage: null
        });
      })
      .catch((caught: unknown) => {
        if (cancelled) {
          return;
        }
        setRecipientsState({
          status: "error",
          recipients: [],
          errorMessage: caught instanceof Error ? caught.message : "Could not load recipients."
        });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Escape closes the modal when not mid-submit. The picker stops the native
  // event when its dropdown is open, so this handler won't fire in that case.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      if (submitting) {
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, submitting, onClose]);

  const amountNumber = useMemo(() => Number.parseInt(form.amount, 10), [form.amount]);
  const amountValid =
    Number.isFinite(amountNumber) && amountNumber > 0 && amountNumber <= availableCoins;
  const formValid = amountValid && form.recipient.selectedID !== "" && availableCoins > 0;

  if (!open) {
    return null;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!formValid || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);

    const trimmedReason = form.reason.trim();
    const payload = {
      target_user_id: form.recipient.selectedID,
      amount: amountNumber,
      ...(trimmedReason !== "" ? { reason: trimmedReason } : {}),
      ...(seasonID !== null && seasonID !== "" ? { season_id: seasonID } : {})
    };
    const idempotencyKey = keyFor(JSON.stringify(payload));

    try {
      const entry = await apiClient.spend(payload, idempotencyKey);
      // Spend's INSERT...RETURNING doesn't include the user JOIN, so the
      // wire entry has target_user_email = null. Patch it from the picked
      // recipient so the optimistic HistoryFeed row shows an email until
      // the next refresh hits the JOIN-backed list path.
      const recipient = recipientsState.recipients.find((r) => r.id === form.recipient.selectedID);
      const patched: LedgerEntry =
        recipient !== undefined && entry.target_user_email === null
          ? { ...entry, target_user_email: recipient.email }
          : entry;
      onSent(patched);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" role="presentation" {...overlayDismiss}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="give-modal-title"
      >
        <header className="modal__header">
          <h2 id="give-modal-title">Give gumballs</h2>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            ×
          </button>
        </header>
        <form className="modal__body" onSubmit={handleSubmit}>
          {isAnonymous && (
            <p className="modal__notice modal__notice--anon">
              You'll appear as <strong>Anonymous</strong> to the recipient.
              Notes are turned off for anonymous gumballs.
            </p>
          )}
          {availableCoins === 0 && (
            <p className="modal__notice">
              You don't have any coins left to spend in this season.
            </p>
          )}

          <UserPicker
            label="Recipient"
            placeholder="Type a name or email…"
            value={form.recipient}
            recipients={recipientsState.recipients}
            loadStatus={recipientsState.status}
            loadErrorMessage={recipientsState.errorMessage}
            loadingMessage="Loading the IC roster…"
            emptyMessage="No other ICs in your tenant yet — your admin can upload a coin CSV to add some."
            noSelectionHint="Pick a person from the list."
            required
            onChange={(next) => setForm((previous) => ({ ...previous, recipient: next }))}
          />

          <div className="form-row">
            <label htmlFor={amountID}>Amount</label>
            <input
              id={amountID}
              type="number"
              min={1}
              max={availableCoins}
              step={1}
              value={form.amount}
              onChange={(event) => setForm((previous) => ({ ...previous, amount: event.target.value }))}
              onFocus={(event) => event.currentTarget.select()}
              required
            />
            <small>You can spend up to {availableCoins} coin{availableCoins === 1 ? "" : "s"} this season.</small>
          </div>
          {!isAnonymous && (
            <div className="form-row">
              <label htmlFor={reasonID}>Note (optional)</label>
              <textarea
                id={reasonID}
                rows={2}
                maxLength={300}
                value={form.reason}
                onChange={(event) => setForm((previous) => ({ ...previous, reason: event.target.value }))}
                placeholder="Why this gumball?"
              />
            </div>
          )}
          {error !== null && <p className="form-row__error">{error}</p>}
          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn--rainbow" disabled={!formValid || submitting}>
              {submitting ? "Sending…" : "Send gumballs"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "idempotency_conflict") {
      return "Something changed — please try again.";
    }
    if (caught.code === "idempotency_in_progress") {
      return "Still processing the previous attempt — wait a moment and retry.";
    }
    if (caught.code === "insufficient_balance") {
      return "You don't have enough coins for that amount.";
    }
    if (caught.code === "invalid_parties" || caught.code === "invalid_target") {
      return "That recipient is no longer an active IC.";
    }
    if (caught.code === "no_active_season" || caught.code === "season_not_active") {
      return "No active season. Try again once your admin opens one.";
    }
    if (caught.status === 503) {
      return "Service is temporarily unavailable — please retry.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Couldn't send gumballs.";
}
